'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { I, Icon } from '../system/icons';
import { TextTrim } from '../system/text-trim';
import { TONE, tintAccent } from '../system/tone';
import { useInteractive } from '../system/interactive';
import { MOTION } from '../system/motion';
import { CONTROL_HEIGHT } from '../system/sizing';
// Three size tiers:
//   xs — badge / status / dense chip. Below the control-height ladder; for
//        non-interactive labels packed into tables, member meta, card overlays.
//        This is the default — most pills in the product are badges.
//   sm — interactive control matching Button size="sm" (CONTROL_HEIGHT.sm).
//        Use when the pill pairs with an sm Button on the same row.
//   md — interactive control matching Button size="md" (CONTROL_HEIGHT.md).
//        Use for filter chips, segmented controls, status pills sitting in
//        a card footer next to an md CTA.
// Height is enforced for sm/md (so they sit on the control ladder); xs sizes
// itself off padding + font so badges stay visually distinct from controls.
const PILL_SIZES = {
    xs: {
        font: 'var(--type-label-sm)',
        paddingX: 9,
        paddingRightRemovable: 6,
        gap: 6,
        iconSize: 12,
        removeIconSize: 14,
        removeBoxSize: 16,
        // 12px label-sm + 8px vertical padding = 20px. Locked so pills with and
        // without icons stay consistent — text-box-trim makes trimmed text shorter
        // than the 12px icon, causing height divergence when height is null.
        height: 20,
    },
    sm: {
        font: 'var(--type-label-sm)',
        paddingX: 10,
        paddingRightRemovable: 7,
        gap: 6,
        iconSize: 12,
        removeIconSize: 14,
        removeBoxSize: 18,
        height: CONTROL_HEIGHT.sm,
    },
    md: {
        font: 'var(--type-label)',
        paddingX: 14,
        paddingRightRemovable: 10,
        gap: 8,
        iconSize: 14,
        removeIconSize: 16,
        removeBoxSize: 20,
        height: CONTROL_HEIGHT.md,
    },
};
export const Pill = forwardRef(function Pill({ tone = 'neutral', size = 'xs', icon, selected, onClick, onRemove, removeAriaLabel, href, linkAs, disabled, children, style, className, ...rest }, ref) {
    const sizing = PILL_SIZES[size];
    const t = TONE[tone];
    const removable = !!onRemove;
    const linkable = !!href && !disabled;
    const interactive = removable || !!onClick || linkable;
    // Pills frequently sit inside a parent link or clickable row (MemberRow,
    // CardLink). Without stopPropagation, clicking an interactive pill also
    // navigates the wrapping link — a class of bug we hit repeatedly.
    const handleClick = interactive
        ? (e) => {
            e.stopPropagation();
            const inner = onRemove ?? onClick;
            if (inner)
                inner(e);
        }
        : undefined;
    const { state, bind } = useInteractive({
        disabled,
        onClick: handleClick,
    });
    const { hover } = state;
    // Tonal hairline (matches DateBlock). Neutral falls back to var(--border)
    // since tintAccent(neutral, 25) is too faint on white cards.
    const idleBorderColor = tone === 'neutral'
        ? 'var(--border)'
        : tone === 'muted'
            ? 'var(--surface-neutral-border)'
            : tintAccent(tone, 25);
    // xs sits below the control ladder — it pads itself off the font so badges
    // stay visually distinct from interactive controls. sm/md lock height to
    // CONTROL_HEIGHT to pair cleanly with Button at the same size.
    const padding = sizing.height === null
        ? (removable
            ? `4px ${sizing.paddingRightRemovable}px 4px ${sizing.paddingX}px`
            : `4px ${sizing.paddingX}px`)
        : (removable
            ? `0 ${sizing.paddingRightRemovable}px 0 ${sizing.paddingX}px`
            : `0 ${sizing.paddingX}px`);
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizing.gap,
        background: t.bg,
        font: sizing.font,
        color: t.ink,
        ...(sizing.height !== null ? { height: sizing.height } : {}),
        padding,
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${idleBorderColor}`,
        width: 'fit-content',
        alignSelf: 'flex-start',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: MOTION.transition,
    };
    if (!interactive) {
        return (_jsxs("span", { ref: ref, className: className, style: { ...base, ...style }, ...rest, children: [icon && _jsx(Icon, { size: sizing.iconSize, children: icon }), _jsx(TextTrim, { children: children })] }));
    }
    const interactiveStyle = {
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: selected ? t.ink : t.bg,
        color: selected ? t.bg : t.ink,
        // Idle borders match the tonal hairline; hover/selected darken to ink.
        borderColor: selected ? t.ink : hover ? t.ink : idleBorderColor,
        opacity: disabled ? 0.5 : 1,
        textDecoration: 'none',
    };
    const inner = (_jsxs(_Fragment, { children: [icon && _jsx(Icon, { size: sizing.iconSize, children: icon }), _jsx(TextTrim, { children: children }), removable && (_jsx("span", { "aria-hidden": true, style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: sizing.removeBoxSize,
                    height: sizing.removeBoxSize,
                    borderRadius: 'var(--radius-full)',
                    marginLeft: 2,
                    background: hover ? 'rgba(0,0,0,.08)' : 'transparent',
                    transition: MOTION.transition,
                }, children: _jsx(Icon, { size: sizing.removeIconSize, children: I.x }) }))] }));
    if (linkable && href) {
        const LinkAs = (linkAs ?? 'a');
        return (_jsx(LinkAs, { ref: ref, href: href, className: className || undefined, style: { ...base, ...interactiveStyle, ...style }, ...bind, ...rest, children: inner }));
    }
    const ariaLabel = removable
        ? removeAriaLabel ??
            (typeof children === 'string' ? `Remove ${children}` : 'Remove filter')
        : undefined;
    return (_jsx("button", { ref: ref, type: "button", disabled: disabled, "aria-label": ariaLabel, className: className || undefined, style: { ...base, ...interactiveStyle, ...style }, ...bind, ...rest, children: inner }));
});
//# sourceMappingURL=Pill.js.map